// Command palette (Cmd/Ctrl+K). Stub: pages, sub-pages, filters and actions from the
// module registry. Entity search (customers, items) arrives with the API.
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { Search } from 'lucide-react';
import { modulesFor } from '../config/modules';
import { useAuth } from '../auth/context';
import { useUi } from '../stores/ui';
import { COMPARES, PERIODS, useFilters } from '../stores/filters';

interface Entry {
  id: string;
  title: string;
  group: 'Pages' | 'Filters' | 'Actions';
  subtitle?: string;
  run: () => void;
}

export function CommandPalette() {
  const open = useUi((s) => s.paletteOpen);
  const setOpen = useUi((s) => s.setPaletteOpen);
  const toggleTheme = useUi((s) => s.toggleTheme);
  const theme = useUi((s) => s.theme);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(0);
  const input = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setOpen(!useUi.getState().paletteOpen);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [setOpen]);

  useEffect(() => {
    if (open) {
      setQ('');
      setSel(0);
      setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);

  const entries = useMemo<Entry[]>(() => {
    const out: Entry[] = [];
    for (const m of modulesFor(user?.role)) {
      out.push({ id: m.key, title: m.label, subtitle: m.group, group: 'Pages', run: () => navigate(m.path) });
      for (const t of m.tabs ?? [])
        out.push({
          id: `${m.key}/${t.id}`,
          title: t.title,
          subtitle: m.label,
          group: 'Pages',
          run: () => navigate(`${m.path}/${t.id}`),
        });
    }
    for (const p of PERIODS)
      out.push({
        id: `p-${p.id}`,
        title: `Period: ${p.label}`,
        group: 'Filters',
        run: () => useFilters.getState().setPeriod(p.id),
      });
    for (const c of COMPARES)
      out.push({
        id: `c-${c.id}`,
        title: `Compare to ${c.label}`,
        group: 'Filters',
        run: () => useFilters.getState().setCompare(c.id),
      });
    out.push({
      id: 'theme',
      title: `Switch to ${theme === 'dark' ? 'light' : 'dark'} appearance`,
      group: 'Actions',
      run: toggleTheme,
    });
    out.push({ id: 'print', title: 'Print this page', group: 'Actions', run: () => window.print() });
    return out;
  }, [user?.role, navigate, theme, toggleTheme]);

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return entries.filter((e) => e.group !== 'Filters');
    return entries.filter((e) => `${e.title} ${e.subtitle ?? ''}`.toLowerCase().includes(needle));
  }, [entries, q]);

  if (!open) return null;
  const run = (e: Entry | undefined) => {
    if (!e) return;
    setOpen(false);
    e.run();
  };
  return (
    <>
      <div className="sheet-backdrop" onClick={() => setOpen(false)} />
      <div className="cmdk" role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="cmdk-in">
          <Search size={18} color="var(--label-2)" />
          <input
            ref={input}
            value={q}
            placeholder="Search pages, filters, actions"
            aria-label="Search"
            role="combobox"
            aria-expanded="true"
            aria-controls="cmdk-list"
            aria-activedescendant={results[sel] ? `cmdk-${results[sel].id}` : undefined}
            onChange={(e) => {
              setQ(e.target.value);
              setSel(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpen(false);
              else if (e.key === 'ArrowDown') {
                e.preventDefault();
                setSel((s) => Math.min(results.length - 1, s + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setSel((s) => Math.max(0, s - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                run(results[sel]);
              }
            }}
          />
          <kbd>esc</kbd>
        </div>
        <div className="cmdk-list" id="cmdk-list" role="listbox">
          {!results.length && <div className="cmdk-empty">No matches for "{q}"</div>}
          {results.map((e, i) => (
            <button
              key={e.id}
              id={`cmdk-${e.id}`}
              type="button"
              role="option"
              aria-selected={i === sel}
              className={`menu-item${i === sel ? ' kb' : ''}`}
              onMouseMove={() => setSel(i)}
              onClick={() => run(e)}
            >
              <span>{e.title}</span>
              {e.subtitle && (
                <span className="t-cap" style={{ marginLeft: 'auto' }}>
                  {e.subtitle}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}
